import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight, Zap } from 'lucide-react';
import { services, partners, certifications, heroImage } from '../data/mock';
import { useT } from '../i18n/LanguageContext';

const Services = () => {
  const { lang } = useT();

  const currentLang = ['fr', 'en', 'ar'].includes(lang) ? lang : 'fr';

  const wired = [
    'courants-forts',
    'courants-faibles',
    'energies-renouvelables',
    'mobilite-electrique',
    'renovation-energetique',
    'chauffage-climatisation',
    'vrd',
    'bureau-etudes',
  ];

  const sectionText = {
    fr: {
      badge: '+ NOTRE EXPERTISE',
      titleA: 'Solutions complètes,',
      titleB: 'de la conception à la livraison.',
      subtitle:
        'Huit métiers complémentaires pour répondre à tous vos besoins en énergie et en bâtiment.',
      learnMore: 'En savoir plus',
    },

    en: {
      badge: '+ OUR EXPERTISE',
      titleA: 'Complete solutions,',
      titleB: 'from design to delivery.',
      subtitle:
        'Eight complementary areas of expertise to meet all your energy and building needs.',
      learnMore: 'Learn more',
    },

    ar: {
      badge: '+ خبرتنا',
      titleA: 'حلول متكاملة،',
      titleB: 'من التصميم إلى التنفيذ.',
      subtitle:
        'ثمانية مجالات متكاملة لتلبية جميع احتياجاتكم في مجال الطاقة والمباني.',
      learnMore: 'اكتشف المزيد',
    },
  };

  const serviceTranslations = {
    fr: {
      'courants-forts': {
        title: 'Courants forts',
        desc:
          'Distribution électrique, tableaux BT/HT, éclairage et équipements de puissance pour bâtiments tertiaires et industriels.',
      },

      'courants-faibles': {
        title: 'Courants faibles',
        desc:
          'Sûreté, contrôle d’accès, réseaux VDI, vidéosurveillance et systèmes de communication pour vos infrastructures.',
      },

      'energies-renouvelables': {
        title: 'Énergies renouvelables',
        desc:
          'Solutions photovoltaïques et stockage batterie pour accélérer votre transition énergétique vers des sources durables.',
      },

      'mobilite-electrique': {
        title: 'Mobilité électrique',
        desc:
          'Bornes de recharge pour véhicules électriques : de la wallbox résidentielle aux stations rapides en réseau (IRVE).',
      },

      'renovation-energetique': {
        title: 'Rénovation énergétique',
        desc:
          'Audit, conseil et travaux de rénovation pour réduire la consommation et valoriser durablement vos bâtiments.',
      },

      'chauffage-climatisation': {
        title: 'Chauffage et climatisation',
        desc:
          'Solutions CVC complètes : pompes à chaleur, climatisation et ventilation, de l’installation à la maintenance.',
      },

      vrd: {
        title: 'VRD (Voiries et Réseaux Divers)',
        desc:
          'Voiries, réseaux divers et aménagements extérieurs : terrassement, drainage et infrastructures de site.',
      },

      'bureau-etudes': {
        title: "Bureau d'études",
        desc:
          'Ingénierie électrique, conception, dimensionnement et suivi de chantier au service de vos projets énergétiques.',
      },
    },

    en: {
      'courants-forts': {
        title: 'Power systems',
        desc:
          'Electrical distribution, LV/MV switchboards, lighting and power equipment for commercial and industrial buildings.',
      },

      'courants-faibles': {
        title: 'Low-voltage systems',
        desc:
          'Security, access control, VDI networks, video surveillance and communication systems for your infrastructure.',
      },

      'energies-renouvelables': {
        title: 'Renewable energy',
        desc:
          'Photovoltaic and battery storage solutions to accelerate your transition towards sustainable energy sources.',
      },

      'mobilite-electrique': {
        title: 'Electric mobility',
        desc:
          'Electric vehicle charging stations, from residential wallboxes to networked fast-charging stations.',
      },

      'renovation-energetique': {
        title: 'Energy renovation',
        desc:
          'Audits, consulting and renovation works to reduce energy consumption and sustainably enhance your buildings.',
      },

      'chauffage-climatisation': {
        title: 'Heating & air conditioning',
        desc:
          'Complete HVAC solutions including heat pumps, air conditioning and ventilation, from installation to maintenance.',
      },

      vrd: {
        title: 'Roads & utility networks',
        desc:
          'Roadworks, utility networks and external infrastructure including earthworks, drainage and site development.',
      },

      'bureau-etudes': {
        title: 'Engineering office',
        desc:
          'Electrical engineering, design, sizing and site supervision for your energy projects.',
      },
    },

    ar: {
      'courants-forts': {
        title: 'التيارات الكهربائية القوية',
        desc:
          'توزيع الكهرباء ولوحات الجهد المنخفض والمتوسط والإضاءة ومعدات الطاقة للمباني التجارية والصناعية.',
      },

      'courants-faibles': {
        title: 'التيارات الكهربائية الضعيفة',
        desc:
          'أنظمة الأمن والتحكم في الدخول وشبكات الاتصالات والمراقبة بالفيديو وأنظمة الاتصال للبنية التحتية.',
      },

      'energies-renouvelables': {
        title: 'الطاقات المتجددة',
        desc:
          'حلول الطاقة الشمسية الكهروضوئية وتخزين البطاريات لتسريع التحول نحو مصادر الطاقة المستدامة.',
      },

      'mobilite-electrique': {
        title: 'التنقل الكهربائي',
        desc:
          'محطات شحن السيارات الكهربائية، من الشواحن المنزلية إلى محطات الشحن السريع.',
      },

      'renovation-energetique': {
        title: 'تجديد الطاقة',
        desc:
          'التدقيق والاستشارات وأعمال التجديد لتقليل استهلاك الطاقة وتحسين قيمة المباني بشكل مستدام.',
      },

      'chauffage-climatisation': {
        title: 'التدفئة وتكييف الهواء',
        desc:
          'حلول متكاملة للتدفئة والتهوية وتكييف الهواء، من التركيب إلى الصيانة.',
      },

      vrd: {
        title: 'الطرق والشبكات المختلفة',
        desc:
          'أعمال الطرق والشبكات والبنية التحتية الخارجية، بما في ذلك الحفر والتصريف وتجهيز المواقع.',
      },

      'bureau-etudes': {
        title: 'مكتب الدراسات الهندسية',
        desc:
          'الهندسة الكهربائية والتصميم والحسابات الفنية ومتابعة تنفيذ مشاريع الطاقة.',
      },
    },
  };

  const heroText = {
    fr: {
      badge: 'GPE · ÉNERGIES & SERVICES',
      titleA: 'Des solutions complètes',
      titleAccent: 'pour vos projets énergétiques.',
      subtitle:
        'Nous accompagnons les entreprises, collectivités et industries avec des solutions fiables, innovantes et durables.',
      breadcrumb: 'Services',
      home: 'Accueil',
      projectTitle: 'Vous avez un projet ?',
      projectSub:
        'Notre équipe d’experts est à votre disposition pour étudier vos besoins et vous proposer une solution adaptée.',
      projectButton: 'DEMANDER UN DEVIS',
      trustTitleA: 'Eux aussi nous font',
      trustTitleAccent: 'confiance…',
      certificationsTagline: 'GPE ÉNERGIES & SERVICES',
      certificationsTitleA: 'Nos',
      certificationsTitleAccent: 'certifications',
    },
    en: {
      badge: 'GPE · ENERGIES & SERVICES',
      titleA: 'Complete solutions',
      titleAccent: 'for your energy projects.',
      subtitle:
        'We support companies, local authorities and industries with reliable, innovative and sustainable solutions.',
      breadcrumb: 'Services',
      home: 'Home',
      projectTitle: 'Have a project?',
      projectSub:
        'Our team of experts is here to study your needs and propose the right solution.',
      projectButton: 'REQUEST A QUOTE',
      trustTitleA: 'They also trust',
      trustTitleAccent: 'us…',
      certificationsTagline: 'GPE ENERGIES & SERVICES',
      certificationsTitleA: 'Our',
      certificationsTitleAccent: 'certifications',
    },
    ar: {
      badge: 'GPE · الطاقات والخدمات',
      titleA: 'حلول متكاملة',
      titleAccent: 'لمشاريعكم في مجال الطاقة.',
      subtitle:
        'نرافق الشركات والجهات والمؤسسات الصناعية بحلول موثوقة ومبتكرة ومستدامة.',
      breadcrumb: 'الخدمات',
      home: 'الرئيسية',
      projectTitle: 'لديكم مشروع؟',
      projectSub:
        'فريق خبرائنا جاهز لدراسة احتياجاتكم واقتراح الحل المناسب.',
      projectButton: 'طلب عرض سعر',
      trustTitleA: 'هم أيضاً يثقون',
      trustTitleAccent: 'بنا…',
      certificationsTagline: 'GPE · الطاقات والخدمات',
      certificationsTitleA: 'شهاداتنا',
      certificationsTitleAccent: '',
    },
  };

  const hero = heroText[currentLang];

  const section = sectionText[currentLang];
  const translations = serviceTranslations[currentLang];

  return (
    <section
      id="services"
      className="bg-white"
      dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
    >

      {/* =====================================================
          HERO
      ===================================================== */}

     <section className="relative overflow-hidden bg-[#0b1c3a]">

  {/* HERO BACKGROUND */}
  <div className="absolute inset-0">

    <img
      src={heroImage}
      alt={hero.titleA}
      loading="eager"
      className="w-full h-full object-cover"
    />

    {/* DARK BLUE OVERLAY */}
    <div className="absolute inset-0 bg-gradient-to-b from-[#061735]/95 via-[#0b1c3a]/90 to-[#0b1c3a]/90" />

    {/* BOTTOM FADE — MATCHES REFERENCES HERO */}
    <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent via-white/55 to-white" />

  </div>

  {/* HERO CONTENT */}
  <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-24 md:pt-40 md:pb-28">

    {/* BREADCRUMB */}
    <div className="mb-8 text-white/75 text-base md:text-lg">

      <Link
        to="/"
        className="hover:text-white transition-colors"
      >
        ← {hero.home}
      </Link>

      <span className="mx-2">›</span>

      <span className="text-[#f5c518]">
        {hero.breadcrumb}
      </span>

    </div>

    {/* BADGE */}
    <span
      className="
        inline-flex
        items-center
        px-4
        py-2
        rounded-full
        border
        border-white/25
        bg-white/10
        text-white
        text-[11px]
        tracking-[0.25em]
        font-bold
        uppercase
      "
    >
      {hero.badge}
    </span>

    {/* TITLE */}
    <h1
      className="
        mt-7
        max-w-5xl
        text-white
        font-black
        leading-[0.98]
        tracking-tight
        text-5xl
        md:text-6xl
        lg:text-7xl
      "
    >
      {hero.titleA}{' '}

      <span className="font-serif italic text-[#f5c518]">
        {hero.titleAccent}
      </span>
    </h1>

    {/* SUBTITLE */}
    <p
      className="
        mt-7
        max-w-3xl
        text-lg
        md:text-xl
        text-white/80
        leading-relaxed
      "
    >
      {hero.subtitle}
    </p>

  </div>

</section>

      <div className="bg-[#f7f8fb] py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* SECTION BADGE */}
        <span
          className="
            inline-block
            px-3
            py-1
            rounded-full
            border
            border-slate-200
            bg-white
            text-slate-600
            text-[11px]
            tracking-[0.25em]
            font-semibold
          "
        >
          {section.badge}
        </span>

        {/* HEADING */}
        <h2
          className="
            mt-6
            max-w-4xl
            text-4xl
            md:text-5xl
            lg:text-[56px]
            font-black
            !text-[#0b1c3a]
            leading-[1.05]
            tracking-tight
          "
        >
          {section.titleA}
          <br />
          {section.titleB}
        </h2>

        {/* DESCRIPTION */}
        <p
          className="
            mt-5
            max-w-3xl
            text-base
            md:text-lg
            !text-slate-600
            leading-relaxed
          "
        >
          {section.subtitle}
        </p>

        {/* SERVICES GRID */}
        <div
          className="
            mt-12
            grid
            sm:grid-cols-2
            lg:grid-cols-4
            gap-6
          "
        >
          {services.map((s) => {
            const hasPage = wired.includes(s.slug);

            const linkTo = hasPage
              ? `/services/${s.slug}`
              : '/services';

            const translatedService = translations[s.slug];

            const title =
              translatedService?.title || s.title;

            const description =
              translatedService?.desc || s.desc;

            return (
              <Link
                to={linkTo}
                key={s.slug || s.title}
                className="
                  group
                  block
                  bg-white
                  rounded-2xl
                  overflow-hidden
                  border
                  border-slate-200
                  hover:shadow-xl
                  hover:-translate-y-1
                  transition-all
                  duration-300
                "
              >

                {/* IMAGE */}
                <div className="relative aspect-[4/3] overflow-hidden">

                  <img
                    src={s.image}
                    alt={title}
                    loading="lazy"
                    className="
                      w-full
                      h-full
                      object-cover
                      group-hover:scale-105
                      transition-transform
                      duration-500
                    "
                  />

                  {/* BLUE ICON */}
                  <div
                    className="
                      absolute
                      top-3
                      left-3
                      w-10
                      h-10
                      rounded-xl
                      bg-[#0b3b8c]
                      text-white
                      flex
                      items-center
                      justify-center
                      shadow-md
                    "
                  >
                    <Zap
                      className="w-5 h-5"
                      strokeWidth={2.5}
                    />
                  </div>

                </div>

                {/* CARD CONTENT */}
                <div className="p-5">

                  {/* TITLE */}
                  <h3
                    className="
                      !text-[#0b1c3a]
                      font-bold
                      text-base
                      md:text-lg
                      leading-tight
                    "
                  >
                    {title}
                  </h3>

                  {/* DESCRIPTION */}
                  <p
                    className="
                      mt-2
                      !text-slate-500
                      text-sm
                      md:text-[15px]
                      leading-relaxed
                      line-clamp-3
                    "
                  >
                    {description}
                  </p>

                  {/* LEARN MORE */}
                  <span
                    className="
                      mt-4
                      inline-flex
                      items-center
                      gap-1.5
                      !text-[#0b3b8c]
                      font-semibold
                      text-sm
                      group-hover:gap-2
                      transition-all
                    "
                  >
                    {section.learnMore}

                    <ArrowUpRight
                      className="w-4 h-4"
                    />
                  </span>

                </div>
              </Link>
            );
          })}
        </div>

        </div>
      </div>

      {/* =====================================================
          PROJECT CTA
      ===================================================== */}

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="relative overflow-hidden rounded-3xl bg-[#0b1c3a] px-8 py-12 md:px-14 md:py-14 text-white flex flex-wrap items-center justify-between gap-8">
            <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_50%,#0b3b8c_0%,transparent_45%),radial-gradient(circle_at_80%_50%,#17469c_0%,transparent_45%)]" />

            <div className="relative max-w-3xl">
              <h2 className="text-2xl md:text-4xl font-black tracking-tight">
                {hero.projectTitle}
              </h2>

              <p className="mt-3 text-white/70 text-base md:text-lg leading-relaxed">
                {hero.projectSub}
              </p>
            </div>

            <Link
              to="/contact"
              className="relative inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#0b4aa2] text-white font-bold text-sm hover:bg-[#155fc2] transition-colors"
            >
              {hero.projectButton}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* =====================================================
          PARTNERS
      ===================================================== */}

      <section className="py-20 bg-[#f7f8fb]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="text-3xl md:text-5xl font-black text-[#0b1c3a] tracking-tight">
            {hero.trustTitleA}{' '}
            <span className="font-serif italic text-[#17469c]">
              {hero.trustTitleAccent}
            </span>
          </h2>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {partners.slice(0, 6).map((partner) => (
              <div
                key={`${partner.name}-${partner.logo}`}
                className="h-32 bg-white rounded-2xl border border-slate-200 flex items-center justify-center p-5 shadow-sm"
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  loading="lazy"
                  className="max-w-full max-h-20 object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          CERTIFICATIONS
      ===================================================== */}

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center">
            <div className="text-[11px] md:text-xs tracking-[0.3em] font-bold text-[#0b3b8c] uppercase">
              {hero.certificationsTagline}
            </div>

            <h2 className="mt-4 text-4xl md:text-6xl font-black text-[#0b1c3a] tracking-tight">
              {hero.certificationsTitleA}{' '}
              <span className="font-serif italic text-[#17469c]">
                {hero.certificationsTitleAccent}
              </span>
            </h2>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert) => (
              <div
                key={`${cert.name}-${cert.logo}`}
                className="h-64 bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center justify-center p-8"
              >
                <img
                  src={cert.logo}
                  alt={cert.name}
                  loading="lazy"
                  className="max-w-full max-h-36 object-contain"
                />
                <div className="mt-6 h-1 w-full rounded-full bg-[#17469c]" />
              </div>
            ))}
          </div>
        </div>
      </section>

    </section>
  );
};

export default Services;